import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useOrderFilters from '../../utils/useOrderFilters';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Portal from '@material-ui/core/Portal';
import OrderItemCard, { OrderItemCardSkeleton } from '../OrderItemCard';
import ChangeRouteDrawer from './ChangeRouteDrawer';
import OrderItemPreview from './OrderItemPreview';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import convertFilterToConstraint from '@afosto/utils-react/datagrid/convertFilterToConstraint';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import createDefaultView from '@afosto/app-components-react/DataSource/modules/views/utils';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import isDefined from '@afosto/utils/isDefined';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import { items as itemsApi } from '@afosto/order/api/Items';
import { filters as filtersApi } from '@afosto/order/api/Filters';
import {
  addressFormatter,
  claimLocationFormatter,
  invoicesFormatter,
  ordersFormatter,
  productImageFormatter,
  productSkuFormatter,
  routesFormatter,
  shipmentsFormatter,
  targetFormatter,
  transferFormatter,
  transportsFormatter,
} from './formatters';
import translations from './translations';

const ORDER_ITEMS_PROFILE_MODEL = 'orderitems';
const ORDER_ITEMS_PROFILE_PATH = 'odr.orders.orderitems';

const ItemsContainer = props => <Grid container spacing={1} {...props} />;

const OrderItemsGrid = ({
  bulkActions,
  order,
  calculation,
  actions,
  modules,
  onAddSelection,
  onRemoveSelection,
  previewContainer,
  shouldReloadItems,
  setShouldReloadItems,
}) => {
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const subscriptions = useSubscriptions();
  const hasWmsSubscription = subscriptions.some(subscription => subscription.code === 'wms');
  const { isFetchingProfile, views } = useDataSourceProfile({
    model: ORDER_ITEMS_PROFILE_MODEL,
    profilePath: ORDER_ITEMS_PROFILE_PATH,
    shouldFetchDefinitions: false,
  });
  const [additionalFilters, additionalProperties, isLoadingOrderFilters] = useOrderFilters();
  const [orderItem, setOrderItem] = useState(null);
  const [showChangeRouteDrawer, setShowChangeRouteDrawer] = useState(false);
  const isLoading = isFetchingProfile || isLoadingOrderFilters;

  const defaultFilters = [
    {
      key: 'skus',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.skus',
        defaultMessage: "SKU's",
        description: 'The filter name for column skus',
      }),
      operators: ['in'],
      type: 'sku',
    },
    {
      key: 'address_id',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.address',
        defaultMessage: 'Address',
        description: 'The filter name for column address',
      }),
      operators: ['in'],
      type: 'address',
    },
    {
      key: 'order_ids',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.orderIds',
        defaultMessage: "Order ID's",
        description: 'The filter name for column order ids',
      }),
      operators: ['in'],
      type: 'order',
    },
    {
      key: 'order_count',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.orderCount',
        defaultMessage: 'Order count',
        description: 'The filter name for column order count',
      }),
      type: 'number',
      operators: ['in', 'gte', 'lte'],
    },
    {
      key: 'is_unavailable',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.isUnavailable',
        defaultMessage: 'Unavailable',
        description: 'The filter name for column is unavailable',
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'is_backorder',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.isBackorder',
        defaultMessage: 'Backorder',
        description: 'The filter name for column is backorder',
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'is_backordered',
      name: intl.formatMessage({
        id: `dataGrid.columns.items.isBackordered`,
        defaultMessage: 'Backordered',
        description: `The filter name for column is backordered`,
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'is_inbound',
      name: intl.formatMessage({
        id: `dataGrid.columns.items.isInbound`,
        defaultMessage: 'Inbound',
        description: `The filter name for column is inbound`,
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'target',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.target',
        defaultMessage: 'Target',
        description: 'The filter name for column target',
      }),
      operators: ['in'],
      type: 'order',
    },
    {
      key: 'backorder_ids',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items}.backorders',
        defaultMessage: 'Backorders',
        description: 'The filter name for column backorders',
      }),
      operators: ['in'],
      type: 'order',
    },
    {
      key: 'backorder_count',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.backorderCount',
        defaultMessage: 'Backorder count',
        description: 'The filter name for column backorder count',
      }),
      type: 'number',
      operators: ['in', 'gte', 'lte'],
    },
    {
      key: 'shipment_count',
      name: intl.formatMessage({
        id: `dataGrid.columns.items.shipmentCount`,
        defaultMessage: 'Shipment count',
        description: `The filter name for column shipment count`,
      }),
      type: 'number',
      operators: ['in', 'gte', 'lte'],
    },
  ];
  const defaultFilterKeys = defaultFilters.map(defaultFilter => defaultFilter.key);
  const defaultProperties = [
    {
      key: 'sku',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.sku',
        defaultMessage: 'SKU',
        description: 'The column name for column sku',
      }),
      type: 'productSku',
    },
    {
      key: 'description',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.description',
        defaultMessage: 'Description',
        description: 'The column name for column description',
      }),
    },
    {
      key: 'quantity',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.quantity',
        defaultMessage: 'Quantity',
        description: 'The column name for column quantity',
      }),
    },
    {
      key: 'orders',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.orders',
        defaultMessage: 'Orders',
        description: 'The column name for column orders',
      }),
      type: 'orders',
    },
    {
      key: 'backorders',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.backorders',
        defaultMessage: 'Backorders',
        description: 'The column name for column backorders',
      }),
      type: 'orders',
    },
    {
      key: 'invoices',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.invoices',
        defaultMessage: 'Invoices',
        description: 'The column name for column invoices',
      }),
      type: 'invoices',
    },
    {
      key: 'shipments',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.shipments',
        defaultMessage: 'Shipments',
        description: 'The column name for column shipments',
      }),
      type: 'shipments',
    },
    {
      key: 'isAvailable',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.isAvailable',
        defaultMessage: 'Available',
        description: 'The column name for column available',
      }),
      type: 'boolean',
    },
    {
      key: 'isBackorder',
      name: intl.formatMessage({
        id: 'dataGrid.columns.items.isBackorder',
        defaultMessage: 'Backorder',
        description: 'The column name for column is backorder',
      }),
      type: 'boolean',
    },
  ];

  const fetchOrderItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async ({ filters = {} }, requestOptions) => {
        const { activeFilters = [], fixedFilters = [] } = filters;
        const constraints = activeFilters
          .filter(activeFilter => defaultFilterKeys.includes(activeFilter.key))
          .reduce((acc, activeFilter) => [...acc, convertFilterToConstraint(activeFilter)], []);

        const fixedConstraints = fixedFilters.reduce(
          (acc, fixedFilter) => [...acc, convertFilterToConstraint(fixedFilter)],
          [],
        );

        const orderFilters = activeFilters
          .filter(activeFilter => !defaultFilterKeys.includes(activeFilter.key))
          .reduce(
            (acc, activeFilter) => [
              ...acc,
              { key: activeFilter?.key, values: activeFilter?.value ?? [] },
            ],
            [],
          );

        const searchKey = hasWmsSubscription ? 'searchGroupsWithRouting' : 'searchGroups';

        return itemsApi[searchKey](
          {
            constraints: [...fixedConstraints, ...constraints],
            filters: orderFilters,
          },
          requestOptions,
        );
      },
      {
        transformData: data => {
          const itemInvoices = (calculation?.items ?? []).reduce((acc, calculationItem) => {
            const orderItemInvoices = (calculationItem?.order_items ?? []).reduce(
              (itemAcc, item) => {
                if (item.invoice_id) {
                  return { ...itemAcc, [item.id]: item.invoice_id };
                }

                return itemAcc;
              },
              {},
            );

            Object.keys(orderItemInvoices).forEach(itemId => {
              if (acc[itemId]) {
                acc[itemId].push(itemId);
              } else {
                acc[itemId] = [orderItemInvoices[itemId]];
              }
            });

            return acc;
          }, {});

          return data.reduce((acc, item) => {
            const { references = {} } = item;
            const orderReference = findObjectInArray(references?.orders ?? [], 'id', order?.id);
            const orders = Object.values(references?.orders ?? [])
              .filter(stack => stack.id !== order.id)
              .map(stack => stack.id);

            const backorders = references?.backorders ?? [];
            const shipments = (orderReference?.shipments ?? []).map(shipment => shipment.id);
            const invoiceIds = (item?.ids ?? []).reduce((itemAcc, itemId) => {
              if (itemInvoices[itemId]) {
                return [...itemAcc, ...itemInvoices[itemId]];
              }

              return itemAcc;
            }, []);
            const invoices = [...new Set(invoiceIds)];

            const filterProperties = (item?.filters ?? []).reduce(
              (filterAcc, filter) => ({ ...filterAcc, [`filter_${filter?.key}`]: filter.values }),
              {},
            );

            const claimId = orderReference?.warehouse?.claim_id;
            const collectRouteId = orderReference?.warehouse?.collect_route_id;
            const deliveryRouteId = orderReference?.warehouse?.delivery_route_id;
            const isReservation = orderReference?.warehouse?.is_reservation;
            const transferId = orderReference?.warehouse?.transfer_id;
            const addressId = orderReference?.warehouse?.address_id;
            const [firstId] = item?.ids ?? [];

            return [
              ...acc,
              {
                ...filterProperties,
                ...item,
                id: firstId,
                isInbound: orderReference?.is_inbound,
                isAvailable: isDefined(orderReference?.is_unavailable)
                  ? !orderReference?.is_unavailable
                  : undefined,
                isBackorder: orderReference?.is_backorder,
                quantity: orderReference?.is_inbound ? item.quantity * -1 : item.quantity,
                orders,
                backorders,
                shipments,
                invoices,
                collectRoute: collectRouteId ? [collectRouteId] : [],
                deliveryRoute: deliveryRouteId ? [deliveryRouteId] : [],
                transfer: transferId,
                address: addressId,
                target: references.target,
                claimId,
                isReservation,
              },
            ];
          }, []);
        },
      },
    );

  const fetchFilterOptions = async filter => {
    try {
      if (defaultFilterKeys.includes(filter.key)) {
        return [];
      }

      const response = await filtersApi.getFilterValues(filter?.key);
      const { data = [] } = response;

      return data.reduce((acc, value) => [...acc, { label: value, value }], []);
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataGrid(fetchOrderItems, {
    gridOptions: {
      columnSelection: true,
      filterSelection: true,
      viewsSelection: true,
      copyGridUrl: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isLoading,
      storeStateKey: 'order_items',
    },
    modules: ['activeItem', 'dataProperties', 'pagination', 'filters', 'views', ...modules],
    pagination: {
      itemsPerPage: 100,
    },
    dataProperties: {
      fixedProperties: [],
      properties: [
        ...defaultProperties,
        {
          key: 'image',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.image',
            defaultMessage: 'Image',
            description: 'The column name for column image',
          }),
          type: 'productImage',
          width: '50px',
        },
        {
          key: 'deliveryRoute',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.deliveryRoute',
            defaultMessage: 'Delivery route',
            description: 'The column name for column delivery route',
          }),
          type: 'routes',
        },
        {
          key: 'collectRoute',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.collectRoute',
            defaultMessage: 'Collect route',
            description: 'The column name for column collect route',
          }),
          type: 'routes',
        },
        {
          key: 'transfer',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.transfer',
            defaultMessage: 'Transfer',
            description: 'The column name for column transfer',
          }),
          type: 'transfer',
        },
        {
          key: 'claimId',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.origin',
            defaultMessage: 'Origin',
            description: 'The column name for column claimId',
          }),
          type: 'claimLocation',
        },
        {
          key: 'transports',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.transports',
            defaultMessage: 'Transports',
            description: 'The column name for column transport',
          }),
          type: 'transports',
        },
        {
          key: 'isReservation',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.isReservation',
            defaultMessage: 'Reservation',
            description: 'The column name for column is reservation',
          }),
          type: 'boolean',
        },
        {
          key: 'isInbound',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.isInbound',
            defaultMessage: 'Inbound',
            description: 'The column name for column is inbound',
          }),
          type: 'boolean',
        },
        {
          key: 'target',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.target',
            defaultMessage: 'Target',
            description: 'The column name for column target',
          }),
          type: 'target',
        },
        {
          key: 'address',
          name: intl.formatMessage({
            id: 'dataGrid.columns.items.address',
            defaultMessage: 'Address',
            description: 'The column name for column address',
          }),
          type: 'address',
        },
        ...additionalProperties,
      ],
    },
    views: {
      canCreateViewForTenant: sessionManager && sessionManager.hasPermissions(['odr:orders:write']),
      defaultView: createDefaultView(intl, {
        properties: defaultProperties,
      }),
      views,
      viewShortcuts: true,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(ORDER_ITEMS_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(ORDER_ITEMS_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(ORDER_ITEMS_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(ORDER_ITEMS_PROFILE_PATH)(repositionedViews),
    },
    filters: {
      filters: [...defaultFilters, ...additionalFilters],
      fixedFilters: [
        {
          key: 'order_ids',
          operator: 'in',
          value: [order?.id],
        },
      ],
      loadFilterOptions: fetchFilterOptions,
    },
    selection: {
      allowSelectAll: false,
      onAddSelection,
      onRemoveSelection,
    },
  });
  const { data, activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  const dataSourceRef = useRef(dataSource);
  const shouldReloadItemsRef = useRef(setShouldReloadItems);

  useEffect(() => {
    dataSourceRef.current = dataSource;
    shouldReloadItemsRef.current = setShouldReloadItems;
  });

  useEffect(() => {
    if (activeItem) {
      const item = findObjectInArray(data, 'id', activeItem);
      setOrderItem(item);
    } else {
      setOrderItem(null);
    }
  }, [activeItem, data]);

  useEffect(() => {
    if (shouldReloadItems && shouldReloadItemsRef.current) {
      if (dataSourceRef.current.hasModule('selection')) {
        dataSourceRef.current.selection.setSelectedItems([]);
      }

      dataSourceRef.current.reloadDataSource();
      shouldReloadItemsRef.current(false);
    }
  }, [shouldReloadItems]);

  const handleRouteChange = () => {
    dataSource.reloadDataSource();
    setShowChangeRouteDrawer(false);
  };

  const hasCollectRoutes = orderItem?.collect_routes?.length > 0;
  const hasDeliveryRoutes = orderItem?.delivery_routes?.length > 0;
  const previewMenu = [
    ...(hasWmsSubscription && (hasCollectRoutes || hasDeliveryRoutes)
      ? [
          {
            label: intl.formatMessage(translations.previewActions.changeRoute),
            icon: 'route',
            shortcut: true,
            onClick: () => setShowChangeRouteDrawer(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={item => <OrderItemCard item={item} />}
          renderSkeletonItem={key => <OrderItemCardSkeleton key={key} />}
          component={ItemsContainer}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          bulkActions={bulkActions}
          actions={actions}
          formatters={{
            address: addressFormatter,
            claimLocation: claimLocationFormatter,
            invoices: invoicesFormatter,
            orders: ordersFormatter,
            backorders: ordersFormatter,
            productSku: productSkuFormatter,
            productImage: productImageFormatter,
            routes: routesFormatter,
            shipments: shipmentsFormatter,
            transfer: transferFormatter,
            target: targetFormatter,
            transports: transportsFormatter,
          }}
        />
      </Hidden>
      <Portal container={previewContainer.current}>
        <OrderItemPreview
          isOpen={activeItem !== null}
          item={orderItem}
          menu={previewMenu}
          onClose={() => activeItemModule.setActiveItem(null)}
        />
      </Portal>
      <ChangeRouteDrawer
        isOpen={showChangeRouteDrawer}
        onCancel={() => setShowChangeRouteDrawer(false)}
        onSave={handleRouteChange}
        order={order}
        orderItem={orderItem}
      />
    </>
  );
};

OrderItemsGrid.propTypes = {
  order: PropTypes.object.isRequired,
  calculation: PropTypes.object,
  bulkActions: PropTypes.array,
  actions: PropTypes.array,
  modules: PropTypes.array,
  onAddSelection: PropTypes.func,
  onRemoveSelection: PropTypes.func,
  previewContainer: PropTypes.object,
  shouldReloadItems: PropTypes.bool,
  setShouldReloadItems: PropTypes.func,
};

OrderItemsGrid.defaultProps = {
  bulkActions: [],
  calculation: undefined,
  actions: [],
  modules: [],
  onAddSelection: undefined,
  onRemoveSelection: undefined,
  previewContainer: undefined,
  shouldReloadItems: false,
  setShouldReloadItems: undefined,
};

export default OrderItemsGrid;
